import Switch from '../../../components/form/Switch'
import PopupSection from '../../../components/popup/PopupSection'
import PopupSectionRow from '../../../components/popup/PopupSectionRow'
import { ChangeEvent, useState } from 'react'
import { updateGroupSettings } from '../../../services/api/group'
import Logger from '../../../services/Logger'
import { AccountDoc, GroupDoc, GroupSettings } from '../../../types/api/changes'
import { Model } from '../../../types/model'
import {
  featureToMinimumTierNeeded,
  isUserAdminInGroup
} from 'utils/api/groups'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { GENERIC_ERROR } from 'utils/strings'
import { GroupPrivacy } from 'model/models/Group'
import { useConnection } from 'components/providers/ConnectionProvider'
import { t } from 'i18next'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import { parseErrorToMessage } from '../eventInfo/EventPopup'
import { Box } from '@mui/material'
import i18n from 'i18n'
import { isTrulyEmpty } from 'utils/object'
import { TierFeatures } from 'utils/api/group_state'
import { groupToProvider, isConvertedGroup, ProviderType } from 'utils/groups'

interface MembersCanProps {
  group: Model<GroupDoc>
  setLoading: (b: boolean) => void
  error: string
  setError: (error: string) => void
  account: Model<AccountDoc>
  groupMetaDataCheck?: () => void
  allCanAddItemsCheck?: () => void
  addParticipantsCheck?: () => void
  eventOwnershipCheck?: () => void
  eventDetailsCheck?: () => void
  metaDataState?: string
  allCanAddItemsState?: string
  addParticipantsState?: string
  eventOwnershipState?: string
  eventDetailsState?: string
}

interface ExplanationAction {
  title: string
  message: string
  cancelAction?: () => void
  confirmAction?: () => void
  continueButton?: string
}

export default function MemberCanSection(props: MembersCanProps) {
  const connection = useConnection()
  Logger.orange('MemberCanSection', props)
  const highestTier = highestActiveTier(props.account)
  Logger.orange('MemberCanSection highestTier', highestTier, props.account)

  const isConvertedCalendar = isConvertedGroup(props.group)

  const [showConvertedExplanation, setShowConvertedExplanation] =
    useState(false)

  const [explanationDialogMessage, setExplanationDialogMessage] = useState<
    ExplanationAction | undefined
  >(undefined)

  async function updateSettings(data: GroupSettings, group: Model<GroupDoc>) {
    if (connection.isConnectionAvailable()) {
      props.setLoading(true)
      try {
        await updateGroupSettings(data, group)
      } catch (e) {
        Logger.red(String(e))
        props.setError(parseErrorToMessage(e))
      }
      props.setLoading(false)
    }
  }

  const toggleChangeGroupMetaData = (checked: boolean) => {
    if (isConvertedCalendar) {
      setShowConvertedExplanation(true)
      return
    }

    updateSettings(
      {
        ...props.group.GroupSettings,
        IsAllParticipantsCanEditGroupMetadata: checked ? '1' : '0'
      },
      props.group
    )

    if (props.groupMetaDataCheck) {
      props.groupMetaDataCheck()
    }
  }

  const toggleAllCanAddItems = (newValue: string) => {
    if (isConvertedCalendar) {
      setShowConvertedExplanation(true)
      return
    }

    let newGroupSettings = {
      ...props.group.GroupSettings,
      IsAllParticipantsCanAddItems: newValue
    }

    if (props.allCanAddItemsCheck) {
      props.allCanAddItemsCheck()
    }

    if (
      newValue === '1' &&
      props.group.GroupSettings?.IsAllParticipantsCanSeeFreeBusyOnly === '1'
    ) {
      newGroupSettings = {
        ...newGroupSettings,
        IsAllParticipantsCanEditOnlyTheirEvents: '1'
      }

      if (props.eventOwnershipCheck) {
        props.eventOwnershipCheck()
      }
    }

    if (
      newValue === '0' &&
      props.group.GroupSettings?.IsAllParticipantsCanEditOnlyTheirEvents === '1'
    ) {
      newGroupSettings = {
        ...newGroupSettings,
        IsAllParticipantsCanEditOnlyTheirEvents: '0'
      }

      if (props.eventOwnershipCheck) {
        props.eventOwnershipCheck()
      }
    }

    updateSettings(newGroupSettings, props.group)
  }

  const toggleAddParticipants = (checked: boolean) => {
    if (isConvertedCalendar) {
      setShowConvertedExplanation(true)
      return
    }

    updateSettings(
      {
        ...props.group.GroupSettings,
        IsAllParticipantsCanAddParticipants: checked ? '1' : '0'
      },
      props.group
    )

    if (props.addParticipantsCheck) {
      props.addParticipantsCheck()
    }
  }

  function performEventDetailsCheck(groupSettings: GroupSettings) {
    updateSettings(groupSettings, props.group)

    if (props.eventDetailsCheck) {
      props.eventDetailsCheck()
    }
  }

  const toggleEventsDetails = (checked: boolean) => {
    if (
      checked &&
      props.group.GroupSettings.IsAllParticipantsCanAddItems === '1' &&
      props.group.GroupSettings.IsAllParticipantsCanEditOnlyTheirEvents === '0'
    ) {
      if (isTrulyEmpty(props.group._id)) {
        performEventDetailsCheck({
          ...props.group.GroupSettings,
          IsAllParticipantsCanSeeFreeBusyOnly: '0'
        })
      }
      setExplanationDialogMessage({
        title: t('eventVisibilityExplanationTitle'),
        message: t('eventVisibilityExplanationBody'),
        cancelAction: () => {
          setExplanationDialogMessage(undefined)
          if (
            props.group.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly !==
            '0'
          ) {
            performEventDetailsCheck({
              ...props.group.GroupSettings,
              IsAllParticipantsCanSeeFreeBusyOnly: '0'
            })
          }
        },
        confirmAction: () => {
          setExplanationDialogMessage(undefined)

          performEventDetailsCheck({
            ...props.group.GroupSettings,
            IsAllParticipantsCanSeeFreeBusyOnly: '1',
            IsAllParticipantsCanEditOnlyTheirEvents: '1'
          })

          if (props.eventOwnershipCheck) {
            props.eventOwnershipCheck()
          }
        }
      })
      return
    }

    performEventDetailsCheck({
      ...props.group.GroupSettings,
      IsAllParticipantsCanSeeFreeBusyOnly: checked ? '1' : '0'
    })
  }

  const toggleEventOwnership = (checked: boolean) => {
    if (isConvertedCalendar) {
      setShowConvertedExplanation(true)
      return
    }

    if (
      !checked &&
      props.group.GroupSettings.IsAllParticipantsCanAddItems === '1' &&
      props.group.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly === '1'
    ) {
      setExplanationDialogMessage({
        title: t('cantDoThat'),
        message: t('editEventsExplanation'),
        cancelAction: undefined,
        confirmAction: () => {
          setExplanationDialogMessage(undefined)
        },
        continueButton: t('ok')
      })

      return
    }

    let newGroupSettings = {
      ...props.group.GroupSettings,
      IsAllParticipantsCanEditOnlyTheirEvents: checked ? '1' : '0'
    }

    if (
      checked &&
      (!props.group.GroupSettings?.IsAllParticipantsCanAddItems ||
        props.group.GroupSettings?.IsAllParticipantsCanAddItems === '0')
    ) {
      newGroupSettings = {
        ...newGroupSettings,
        IsAllParticipantsCanAddItems: '1'
      }

      if (props.allCanAddItemsCheck) {
        props.allCanAddItemsCheck()
      }
    }

    if (props.eventOwnershipCheck) {
      props.eventOwnershipCheck()
    }
    updateSettings(newGroupSettings, props.group)
  }

  const isCurrentGroupThirdParty = isConvertedGroup(props.group)

  if (props.group.PersonalGroupSyncStatus !== '2' && isCurrentGroupThirdParty) {
    return <></>
  }

  const provider = groupToProvider(props.group)

  return props.group._id?.length === 0 ||
    (isUserAdminInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) &&
      props.group.PrivacyMode === GroupPrivacy.PRIVATE) ? (
    <>
      <PopupSection
        title={`${t('membersCan')}`}
        sectionId="membersCanSection"
        titlePadding="0rem 1.8rem"
      >
        <PopupSectionRow
          actionId="add-edit-events-permission"
          title={`${t('addEditEvents')}`}
          tierFeature={TierFeatures.PERMISSIONS_EVENTS}
          neededTier={
            props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
              ? Tier.FREE
              : featureToMinimumTierNeeded(TierFeatures.PERMISSIONS_EVENTS)
          }
          action={() => {
            toggleAllCanAddItems(
              props.group.GroupSettings?.IsAllParticipantsCanAddItems === '1'
                ? '0'
                : '1'
            )
          }}
          value={
            <Switch
              id="add-edit-events-switch"
              checked={
                props.group.GroupSettings?.IsAllParticipantsCanAddItems === '1'
              }
            />
          }
        />
        <Box
          marginLeft={i18n.dir() === 'ltr' ? '16px' : '0px'}
          marginRight={i18n.dir() === 'ltr' ? '0px' : '16px'}
        >
          <PopupSectionRow
            title={`${t('groupSettingsEditOnlyOwners')}`}
            tierFeature={TierFeatures.EDIT_THEIR_EVENTS_ONLY}
            neededTier={
              props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
                ? Tier.FREE
                : featureToMinimumTierNeeded(
                    TierFeatures.EDIT_THEIR_EVENTS_ONLY
                  )
            }
            action={() => {
              toggleEventOwnership(
                props.group.GroupSettings
                  ?.IsAllParticipantsCanEditOnlyTheirEvents === '1'
                  ? false
                  : true
              )
            }}
            value={
              <Switch
                checked={
                  props.group.GroupSettings
                    ?.IsAllParticipantsCanEditOnlyTheirEvents === '1'
                }
              />
            }
          />
        </Box>
        <PopupSectionRow
          actionId="metadata-permission"
          title={`${t('editCalendarName')}`}
          tierFeature={TierFeatures.PERMISSIONS_CALENDAR_NAME}
          neededTier={
            props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
              ? Tier.FREE
              : featureToMinimumTierNeeded(
                  TierFeatures.PERMISSIONS_CALENDAR_NAME
                )
          }
          action={() => {
            toggleChangeGroupMetaData(
              props.group.GroupSettings
                ?.IsAllParticipantsCanEditGroupMetadata === '1'
                ? false
                : true
            )
          }}
          value={
            <Switch
              id="metadata-switch"
              checked={
                props.group.GroupSettings
                  ?.IsAllParticipantsCanEditGroupMetadata === '1'
              }
            />
          }
        />
        <PopupSectionRow
          title={`${t('addNewMembers')}`}
          tierFeature={TierFeatures.PERMISSIONS_MEMBERS}
          neededTier={
            props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
              ? Tier.FREE
              : featureToMinimumTierNeeded(TierFeatures.PERMISSIONS_MEMBERS)
          }
          action={() => {
            toggleAddParticipants(
              props.group.GroupSettings?.IsAllParticipantsCanAddParticipants ===
                '1'
                ? false
                : true
            )
          }}
          value={
            <Switch
              checked={
                props.group.GroupSettings
                  ?.IsAllParticipantsCanAddParticipants === '1'
              }
            />
          }
        />
        <PopupSectionRow
          title={`${t('seeEventsDetails')}`}
          tierFeature={TierFeatures.SEE_EVENTS_DETAILS}
          neededTier={
            props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
              ? Tier.FREE
              : featureToMinimumTierNeeded(TierFeatures.SEE_EVENTS_DETAILS)
          }
          action={() => {
            toggleEventsDetails(
              (props.group.GroupSettings?.IsAllParticipantsCanSeeFreeBusyOnly ??
                '0') === '1'
                ? false
                : true
            )
          }}
          value={
            <Switch
              checked={
                (props.group.GroupSettings
                  ?.IsAllParticipantsCanSeeFreeBusyOnly ?? '0') === '1'
              }
            />
          }
        />
        {explanationDialogMessage && (
          <ConfirmationDialog
            title={explanationDialogMessage.title}
            open
            content={explanationDialogMessage.message}
            leftButtonHidden={
              explanationDialogMessage.cancelAction ? false : true
            }
            rightButton={
              explanationDialogMessage.continueButton
                ? explanationDialogMessage.continueButton
                : t('continue')
            }
            leftButton={t('cancel')}
            handleRightButton={explanationDialogMessage.confirmAction}
            handleLeftButton={explanationDialogMessage.cancelAction}
          />
        )}
        {showConvertedExplanation && (
          <ConfirmationDialog
            title={t('featureNotSupported')}
            open
            content={t('membersCantMakeChanges')}
            leftButtonHidden={true}
            rightButton={t('ok')}
            handleRightButton={() => {
              setShowConvertedExplanation(false)
            }}
          />
        )}
      </PopupSection>
    </>
  ) : (
    <></>
  )
}
